import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useTenant } from "@/contexts/TenantContext";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SERVICE_ORDER_STATUSES } from "@/lib/constants";

const BASE_OPTIONS = SERVICE_ORDER_STATUSES.filter((s) => s.value !== "cancelado");

function BaseSelect({ value, onChange, testId }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-8 w-48" data-testid={testId}><SelectValue placeholder="Estado interno" /></SelectTrigger>
      <SelectContent>{BASE_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function StageRow({ flow, stage, index, call }) {
  const [name, setName] = useState(stage.name);
  const [label, setLabel] = useState(stage.customer_label || "");
  const stages = flow.stages;
  const move = (dir) => {
    const ids = stages.map((s) => s.id);
    [ids[index], ids[index + dir]] = [ids[index + dir], ids[index]];
    call("put", `/service-flows/${flow.id}/stages/order`, { stage_ids: ids });
  };
  const patch = (body) => call("patch", `/service-flows/${flow.id}/stages/${stage.id}`, body);
  return (
    <div className={`flex flex-wrap items-center gap-2 py-2 border-b border-border ${stage.active ? "" : "opacity-50"}`} data-testid={`stage-row-${stage.id}`}>
      <div className="flex flex-col">
        <button disabled={index === 0} onClick={() => move(-1)} data-testid={`stage-up-${stage.id}`}><ArrowUp className="h-3.5 w-3.5" /></button>
        <button disabled={index === stages.length - 1} onClick={() => move(1)} data-testid={`stage-down-${stage.id}`}><ArrowDown className="h-3.5 w-3.5" /></button>
      </div>
      <Input className="h-8 w-56" value={name} onChange={(e) => setName(e.target.value)} onBlur={() => name !== stage.name && patch({ name })} data-testid={`stage-name-${stage.id}`} />
      <Input className="h-8 w-56" placeholder="Etiqueta para el cliente (opcional)" value={label} onChange={(e) => setLabel(e.target.value)}
        onBlur={() => label !== (stage.customer_label || "") && patch({ customer_label: label || null })} data-testid={`stage-label-${stage.id}`} />
      <BaseSelect value={stage.base_status} onChange={(v) => patch({ base_status: v })} testId={`stage-base-${stage.id}`} />
      <div className="flex items-center gap-1 text-xs">
        <Switch checked={stage.active} onCheckedChange={(v) => patch({ active: v })} data-testid={`stage-active-${stage.id}`} /> Activa
      </div>
      <Button size="icon" variant="ghost" onClick={() => call("delete", `/service-flows/${flow.id}/stages/${stage.id}`)} data-testid={`stage-delete-${stage.id}`}>
        <Trash2 className="h-4 w-4 text-red-600" />
      </Button>
    </div>
  );
}

function FlowCard({ flow, call }) {
  const [stage, setStage] = useState({ name: "", base_status: "en_proceso" });
  const stages = [...flow.stages].sort((a, b) => a.position - b.position);
  return (
    <Card className="p-5 border-border space-y-3" data-testid={`flow-card-${flow.id}`}>
      <div className="flex items-center justify-between">
        <div className="font-semibold">{flow.name}</div>
        <div className="flex items-center gap-2 text-xs">
          <Switch checked={flow.active} onCheckedChange={(v) => call("patch", `/service-flows/${flow.id}`, { active: v })} data-testid={`flow-active-${flow.id}`} /> Flujo activo
        </div>
      </div>
      <div>{stages.map((s, i) => <StageRow key={s.id} flow={{ ...flow, stages }} stage={s} index={i} call={call} />)}</div>
      <div className="flex flex-wrap items-center gap-2">
        <Input className="h-8 w-56" placeholder="Nueva etapa" value={stage.name} onChange={(e) => setStage({ ...stage, name: e.target.value })} data-testid={`flow-new-stage-name-${flow.id}`} />
        <BaseSelect value={stage.base_status} onChange={(v) => setStage({ ...stage, base_status: v })} testId={`flow-new-stage-base-${flow.id}`} />
        <Button size="sm" disabled={!stage.name.trim()} onClick={() => call("post", `/service-flows/${flow.id}/stages`, stage).then(() => setStage({ name: "", base_status: "en_proceso" }))} data-testid={`flow-add-stage-${flow.id}`}>
          <Plus className="h-4 w-4 mr-1" /> Agregar etapa
        </Button>
      </div>
      <p className="text-[11px] text-muted-foreground">El estado interno define las reglas (alertas, entrega, cobro). Una etapa ya usada no puede cambiarlo ni borrarse; solo desactivarse.</p>
    </Card>
  );
}

export default function FlujosServicio() {
  const { tenantQueryParam } = useTenant();
  const [flows, setFlows] = useState([]);
  const [templates, setTemplates] = useState([]);

  const load = useCallback(async () => {
    try {
      const [{ data: f }, { data: t }] = await Promise.all([
        api.get("/service-flows", { params: { ...tenantQueryParam, include_inactive: true } }),
        api.get("/service-flows/templates"),
      ]);
      setFlows(f || []);
      setTemplates(t || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [tenantQueryParam]);

  useEffect(() => { load(); }, [load]);

  const call = async (method, url, body) => {
    try {
      await api[method](url, ...(method === "delete" ? [{ params: tenantQueryParam }] : [body, { params: tenantQueryParam }]));
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
      await load();
    }
  };

  return (
    <div className="space-y-6" data-testid="page-flujos-servicio">
      <PageHeader title="Flujos de servicio" description="Etapas por tipo de servicio para las órdenes de este taller." />
      <Card className="p-5 border-border">
        <div className="text-sm font-medium mb-3">Habilitar desde plantilla</div>
        <div className="flex flex-wrap gap-2">
          {templates.map((t) => (
            <Button key={t.key} variant="outline" onClick={() => call("post", "/service-flows/enable", { template_key: t.key })} data-testid={`enable-template-${t.key}`}>
              <Plus className="h-4 w-4 mr-1" /> {t.name} ({t.stages.length} etapas)
            </Button>
          ))}
        </div>
      </Card>
      {flows.length === 0 && <div className="text-sm text-muted-foreground" data-testid="flows-empty">Aún no hay flujos habilitados. Las órdenes usan el flujo general compatible.</div>}
      {flows.map((f) => <FlowCard key={f.id} flow={f} call={call} />)}
    </div>
  );
}
